import {
  CharacterStyleResult,
  CharacterStyleResultRole,
  CharacterStyleStatus,
  CharacterStyleWorkspace,
  CharacterTagOptions,
} from '../types';
import i18n from '../locales';
import { apiErrorFromResponse } from './errors';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

function localizeError(message: string): string {
  const localized = i18n.t(`errors.${message}`, { ns: 'common', defaultValue: '' });
  return localized || message;
}

function getToken(): string {
  return localStorage.getItem('token') || '';
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    throw await apiErrorFromResponse(response, 'Request failed', localizeError);
  }
  return response.json();
}

async function upload<T>(url: string, formData: FormData): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    throw await apiErrorFromResponse(response, 'Upload failed', localizeError);
  }
  return response.json();
}

function characterQuery(characterName: string): string {
  return new URLSearchParams({ characterName }).toString();
}

function characterStylePath(projectId: string, path: string, characterName: string): string {
  return `/projects/${projectId}/character-style${path}?${characterQuery(characterName)}`;
}

function stylingPath(projectId: string, characterName: string, stylingId: string, path = ''): string {
  return characterStylePath(projectId, `/stylings/${encodeURIComponent(stylingId)}${path}`, characterName);
}

export interface CharacterStyleResultListOptions {
  castingImageHash?: string;
  stylingImageHash?: string;
  status?: CharacterStyleStatus;
  page?: number;
  limit?: number;
}

export interface CharacterStyleResultListResponse {
  results: CharacterStyleResult[];
  total: number;
  page: number;
  limit: number;
}

export interface GenerateCharacterStyleResultPayload {
  castingImageHash: string;
  stylingImageHash: string;
  prompt: string;
  refImageUrls?: string[];
  platform?: string;
  model?: string;
}

export interface GenerateCharacterStyleResultResponse {
  result: CharacterStyleResult;
  taskId: string;
}

export interface CharacterStyleResultResponse {
  result: CharacterStyleResult;
}

export const getCharacterStyleWorkspace = (
  projectId: string,
  characterName: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/style-workspace', characterName), { method: 'GET' });

export const retryCharacterStyleInit = (projectId: string): Promise<{ ok: boolean }> =>
  request(`/projects/${projectId}/character-style/init/retry`, { method: 'POST' });

export const retryCharacterStyleTags = (
  projectId: string,
  characterName: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/style-tags/retry', characterName), { method: 'POST' });

export const updateCastingDescription = (
  projectId: string,
  characterName: string,
  description: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/casting-description', characterName), {
    method: 'PUT',
    body: JSON.stringify({ description }),
  });

export const updateStylingDescription = (
  projectId: string,
  stylingId: string,
  characterName: string,
  description: string,
): Promise<CharacterStyleWorkspace> =>
  request(
    characterStylePath(projectId, `/stylings/${stylingId}/description`, characterName),
    { method: 'PUT', body: JSON.stringify({ description }) },
  );

export const fetchCastingCandidates = (
  projectId: string,
  characterName: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/casting-candidates/fetch', characterName), { method: 'POST' });

export const rankCastingCandidates = (
  projectId: string,
  characterName: string,
  force = false,
): Promise<CharacterStyleWorkspace> => {
  const base = characterStylePath(projectId, '/casting-candidates/rank', characterName);
  const url = force ? `${base}&force=true` : base;
  return request(url, { method: 'POST' });
};

export const selectCastingCandidate = (
  projectId: string,
  characterName: string,
  imageHash: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/casting-candidates/select', characterName), {
    method: 'POST',
    body: JSON.stringify({ imageHash }),
  });

export const uploadCastingCandidate = (
  projectId: string,
  characterName: string,
  file: File,
): Promise<CharacterStyleWorkspace> => {
  const formData = new FormData();
  formData.append('file', file);
  return upload(characterStylePath(projectId, '/casting-candidates/upload', characterName), formData);
};

export const createCharacterStyling = (
  projectId: string,
  characterName: string,
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/stylings', characterName), { method: 'POST' });

export const fetchStylingCandidates = (
  projectId: string,
  characterName: string,
  stylingId: string,
): Promise<CharacterStyleWorkspace> =>
  request(stylingPath(projectId, characterName, stylingId, '/candidates/fetch'), { method: 'POST' });

export const selectStylingCandidate = (
  projectId: string,
  characterName: string,
  stylingId: string,
  imageHash: string,
): Promise<CharacterStyleWorkspace> =>
  request(stylingPath(projectId, characterName, stylingId, '/candidates/select'), {
    method: 'POST',
    body: JSON.stringify({ imageHash }),
  });

export const uploadStylingCandidate = (
  projectId: string,
  characterName: string,
  stylingId: string,
  file: File,
): Promise<CharacterStyleWorkspace> => {
  const formData = new FormData();
  formData.append('file', file);
  return upload(stylingPath(projectId, characterName, stylingId, '/candidates/upload'), formData);
};

export const listCharacterStyleResults = (
  projectId: string,
  characterName: string,
  stylingId: string,
  options: CharacterStyleResultListOptions = {},
): Promise<CharacterStyleResultListResponse> => {
  const params = new URLSearchParams();
  params.set('characterName', characterName);
  if (options.castingImageHash) params.set('castingImageHash', options.castingImageHash);
  if (options.stylingImageHash) params.set('stylingImageHash', options.stylingImageHash);
  if (options.status) params.set('status', options.status);
  if (options.page !== undefined) params.set('page', String(options.page));
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  const qs = params.toString();
  return request(`/projects/${projectId}/character-style/stylings/${encodeURIComponent(stylingId)}/results?${qs}`, {
    method: 'GET',
  });
};

export const generateCharacterStyleResult = (
  projectId: string,
  characterName: string,
  stylingId: string,
  payload: GenerateCharacterStyleResultPayload,
): Promise<GenerateCharacterStyleResultResponse> =>
  request(stylingPath(projectId, characterName, stylingId, '/results/generate'), {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const uploadCharacterStyleResult = (
  projectId: string,
  characterName: string,
  stylingId: string,
  castingImageHash: string,
  stylingImageHash: string,
  file: File,
): Promise<CharacterStyleResultResponse> => {
  const formData = new FormData();
  formData.append('castingImageHash', castingImageHash);
  formData.append('stylingImageHash', stylingImageHash);
  formData.append('file', file);
  return upload(stylingPath(projectId, characterName, stylingId, '/results/upload'), formData);
};

export const setCharacterStyleResultRole = (
  projectId: string,
  resultId: string,
  role: CharacterStyleResultRole,
): Promise<{ ok: boolean }> =>
  request(`/projects/${projectId}/character-style-results/${encodeURIComponent(resultId)}/role`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  });

export const getCastingTagOptions = (): Promise<CharacterTagOptions> =>
  request('/character-style/casting-tag-options', { method: 'GET' });

export const getStylingTagOptions = (): Promise<CharacterTagOptions> =>
  request('/character-style/styling-tag-options', { method: 'GET' });

export const updateCastingTags = (
  projectId: string,
  characterName: string,
  tags: string[],
): Promise<CharacterStyleWorkspace> =>
  request(characterStylePath(projectId, '/casting-tags', characterName), {
    method: 'PUT',
    body: JSON.stringify({ tags }),
  });

export const updateStylingTags = (
  projectId: string,
  characterName: string,
  stylingId: string,
  tags: string[],
): Promise<CharacterStyleWorkspace> =>
  request(stylingPath(projectId, characterName, stylingId, '/tags'), {
    method: 'PUT',
    body: JSON.stringify({ tags }),
  });
